import React, { useState, useEffect } from 'react';
import { Key, Copy, RefreshCw, Eye, EyeOff, Check, AlertTriangle, Code, Infinity } from 'lucide-react';

// Check if current user is superadmin
const isSuperadmin = () => localStorage.getItem('ltss_superadmin') === 'true';

interface APIKey {
  id: string;
  name: string;
  key: string;
  createdAt: Date;
  lastUsed: Date | null;
  isActive: boolean;
}

const API_KEYS_STORAGE = 'ltss_api_keys';

// Generate a random API key
function generateAPIKey(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const prefix = 'ltss_';
  let key = prefix;
  for (let i = 0; i < 32; i++) {
    key += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return key;
}

export function APIAccess() {
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [showKey, setShowKey] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [showDocs, setShowDocs] = useState(false);

  // Load API keys from storage
  useEffect(() => {
    const saved = localStorage.getItem(API_KEYS_STORAGE);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setApiKeys(parsed.map((k: any) => ({
          ...k,
          createdAt: new Date(k.createdAt),
          lastUsed: k.lastUsed ? new Date(k.lastUsed) : null,
        })));
      } catch (e) {
        console.error('Failed to load API keys:', e);
      }
    }
  }, []);

  // Save API keys to storage
  const saveKeys = (keys: APIKey[]) => {
    localStorage.setItem(API_KEYS_STORAGE, JSON.stringify(keys));
    setApiKeys(keys);
  };

  const handleCreateKey = () => {
    if (!newKeyName.trim()) return;
    
    const newKey: APIKey = {
      id: Date.now().toString(),
      name: newKeyName.trim(),
      key: generateAPIKey(),
      createdAt: new Date(),
      lastUsed: null,
      isActive: true,
    };
    
    saveKeys([...apiKeys, newKey]);
    setNewKeyName('');
    setShowKey(newKey.id); // Show the new key immediately
  };

  const handleRevokeKey = (id: string) => {
    saveKeys(apiKeys.map(k => k.id === id ? { ...k, isActive: false } : k));
  };

  const handleDeleteKey = (id: string) => {
    saveKeys(apiKeys.filter(k => k.id !== id));
  };

  const handleRegenerateKey = (id: string) => {
    saveKeys(apiKeys.map(k => k.id === id ? { ...k, key: generateAPIKey(), isActive: true } : k));
  };

  const copyToClipboard = async (key: string, id: string) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedKey(id);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (e) {
      console.error('Failed to copy:', e);
    }
  };

  const maskKey = (key: string) => {
    return key.substring(0, 8) + '••••••••••••••••••••••••' + key.substring(key.length - 4);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Key className="w-5 h-5 text-purple-400" />
          <h3 className="text-lg font-medium text-slate-200">API Access</h3>
        </div>
        <button
          onClick={() => setShowDocs(!showDocs)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-slate-400 hover:text-slate-200 transition-colors"
        >
          <Code className="w-4 h-4" />
          {showDocs ? 'Hide' : 'View'} Documentation
        </button>
      </div>

      {/* API Documentation */}
      {showDocs && (
        <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
          <h4 className="text-sm font-medium text-slate-200 mb-3">API Documentation</h4>
          
          <div className="space-y-4 text-sm">
            <div>
              <p className="text-slate-400 mb-2">Base URL:</p>
              <code className="bg-slate-900 px-2 py-1 rounded text-green-400">
                https://api.ltss-analytics.com/v1
              </code>
            </div>

            <div>
              <p className="text-slate-400 mb-2">Authentication:</p>
              <p className="text-slate-300 mb-1">Include your API key in the header:</p>
              <code className="block bg-slate-900 px-3 py-2 rounded text-green-400 overflow-x-auto">
                Authorization: Bearer ltss_your_api_key_here
              </code>
            </div>

            <div>
              <p className="text-slate-400 mb-2">Endpoints:</p>
              <div className="space-y-2">
                <div className="bg-slate-900 px-3 py-2 rounded">
                  <code className="text-blue-400">GET</code>
                  <code className="text-slate-300 ml-2">/reports</code>
                  <p className="text-slate-500 text-xs mt-1">List all uploaded reports</p>
                </div>
                <div className="bg-slate-900 px-3 py-2 rounded">
                  <code className="text-green-400">POST</code>
                  <code className="text-slate-300 ml-2">/reports/upload</code>
                  <p className="text-slate-500 text-xs mt-1">Upload a new LTSS report (multipart/form-data)</p>
                </div>
                <div className="bg-slate-900 px-3 py-2 rounded">
                  <code className="text-blue-400">GET</code>
                  <code className="text-slate-300 ml-2">/reports/:id/alerts</code>
                  <p className="text-slate-500 text-xs mt-1">Get alerts for a specific report</p>
                </div>
                <div className="bg-slate-900 px-3 py-2 rounded">
                  <code className="text-blue-400">GET</code>
                  <code className="text-slate-300 ml-2">/reports/:id/staff</code>
                  <p className="text-slate-500 text-xs mt-1">Get staff metrics for a specific report</p>
                </div>
                <div className="bg-slate-900 px-3 py-2 rounded">
                  <code className="text-blue-400">GET</code>
                  <code className="text-slate-300 ml-2">/reports/:id/financials</code>
                  <p className="text-slate-500 text-xs mt-1">Get financial summary for a specific report</p>
                </div>
                <div className="bg-slate-900 px-3 py-2 rounded">
                  <code className="text-blue-400">GET</code>
                  <code className="text-slate-300 ml-2">/reports/:id/export</code>
                  <p className="text-slate-500 text-xs mt-1">Export report as PDF or JSON</p>
                </div>
              </div>
            </div>

            <div>
              <p className="text-slate-400 mb-2">Example Request:</p>
              <pre className="bg-slate-900 px-3 py-2 rounded text-green-400 overflow-x-auto text-xs">
{`curl -X GET https://api.ltss-analytics.com/v1/reports \\
  -H "Authorization: Bearer ltss_your_api_key_here" \\
  -H "Content-Type: application/json"`}
              </pre>
            </div>

            <div>
              <p className="text-slate-400 mb-2">Rate Limits:</p>
              {isSuperadmin() ? (
                <div className="flex items-center gap-2 text-green-400">
                  <Infinity className="w-4 h-4" />
                  <span className="font-medium">Unlimited (Superadmin)</span>
                </div>
              ) : (
                <ul className="text-slate-300 list-disc list-inside space-y-1">
                  <li>1,000 requests per hour</li>
                  <li>10 concurrent connections</li>
                  <li>50MB max upload size</li>
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create New Key */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-slate-300 mb-3">Create New API Key</h4>
        <div className="flex gap-2">
          <input
            type="text"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            placeholder="Key name (e.g., Production, Development)"
            className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 
                     placeholder-slate-500 text-sm focus:outline-none focus:border-purple-500"
          />
          <button
            onClick={handleCreateKey}
            disabled={!newKeyName.trim()}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-700 
                     disabled:text-slate-500 text-white rounded-lg transition-colors text-sm font-medium"
          >
            Generate Key
          </button>
        </div>
      </div>

      {/* API Keys List */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-slate-300">Your API Keys</h4>
        
        {apiKeys.length === 0 ? (
          <div className="bg-slate-800/50 rounded-lg p-6 text-center">
            <Key className="w-8 h-8 text-slate-600 mx-auto mb-2" />
            <p className="text-slate-400 text-sm">No API keys yet. Create one to get started.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {apiKeys.map((apiKey) => (
              <div
                key={apiKey.id}
                className={`bg-slate-800/50 rounded-lg p-4 border ${
                  apiKey.isActive ? 'border-slate-700' : 'border-red-500/30'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-200">{apiKey.name}</span>
                    {!apiKey.isActive && (
                      <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">
                        Revoked
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {apiKey.isActive && (
                      <>
                        <button
                          onClick={() => setShowKey(showKey === apiKey.id ? null : apiKey.id)}
                          className="p-1.5 text-slate-400 hover:text-slate-200 transition-colors"
                          title={showKey === apiKey.id ? 'Hide key' : 'Show key'}
                        >
                          {showKey === apiKey.id ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => copyToClipboard(apiKey.key, apiKey.id)}
                          className="p-1.5 text-slate-400 hover:text-slate-200 transition-colors"
                          title="Copy key"
                        >
                          {copiedKey === apiKey.id ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleRegenerateKey(apiKey.id)}
                          className="p-1.5 text-slate-400 hover:text-yellow-400 transition-colors"
                          title="Regenerate key"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleRevokeKey(apiKey.id)}
                          className="p-1.5 text-slate-400 hover:text-red-400 transition-colors"
                          title="Revoke key"
                        >
                          <AlertTriangle className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {!apiKey.isActive && (
                      <button
                        onClick={() => handleDeleteKey(apiKey.id)}
                        className="px-2 py-1 text-xs text-red-400 hover:text-red-300 transition-colors"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="font-mono text-sm bg-slate-900 px-3 py-2 rounded">
                  <span className={apiKey.isActive ? 'text-green-400' : 'text-slate-500 line-through'}>
                    {showKey === apiKey.id ? apiKey.key : maskKey(apiKey.key)}
                  </span>
                </div>
                
                <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                  <span>Created: {apiKey.createdAt.toLocaleDateString()}</span>
                  <span>Last used: {apiKey.lastUsed ? apiKey.lastUsed.toLocaleDateString() : 'Never'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Security Notice */}
      <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="text-yellow-400 font-medium">Security Notice</p>
            <p className="text-slate-400 mt-1">
              Keep your API keys secure. Never share them in public repositories or client-side code.
              If a key is compromised, revoke it immediately and generate a new one.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
